/**
 * @typedef {Object} PlayerOptions
 * @property {number} [startTime=Date.now()] Start time in milliseconds
 * @property {number} [endTime=0] End time in milliseconds
 * @property {number} [currentTime=startTime] Current time in milliseconds
 * @property {number} [playbackRate=1] Default playback rate.
 * If the value is negative, the player is played backwards
 * @property {boolean} [autoplay=false] Playback should automatically begin
 * @property {PlayerLoopOptions} [loop] Player loop options
 */

/**
 * @typedef {Object} PlayerLoopOptions
 * @property {number} [startTime] Start time in milliseconds. Default: player current time
 * @property {number} [endTime] End time in milliseconds. Default: player end time
 * @property {boolean} [enabled=false] Playback should automatically begin
 */
const stepTimeRef = Symbol('stepTime');
const rafRef = Symbol('rafId');
export const PLAYER_EVENTS = {
  PLAY: 'play',
  PAUSE: 'pause',
  ENDED: 'ended',
  PLAYBACK_RATE_CHANGE: 'playbackratechange',
  TIME_UPDATE: 'timeupdate'
};

class Player extends EventTarget {
  /**
   * @param {!PlayerOptions} options Player options
   */
  constructor(options = {}) {
    super();
    const now = Date.now();
    this.startTime = now;
    this.endTime = 0;
    this.playbackRate = 1;
    this.autoplay = false;
    this.shouldResumeOnMoveend = false;
    Object.assign(this, options);
    this.currentTime = options.currentTime || this.startTime;
    this.playing = this.autoplay;
    this.nextTick = this.nextTick.bind(this);
    this[stepTimeRef] = now;
    this.loop = Object.assign({
      startTime: this.startTime,
      endTime: this.endTime,
      enabled: false
    }, options.loop);
    let {
      playbackRate,
      currentTime
    } = this;
    Object.defineProperty(this, 'currentTime', {
      get() {
        return currentTime;
      },

      set(timestamp) {
        if (typeof timestamp !== 'number') {
          return;
        }

        currentTime = this.endTime ? Math.min(this.endTime, timestamp) : timestamp;
        this.dispatchEvent(PLAYER_EVENTS.TIME_UPDATE);

        if (this.endTime && currentTime >= this.endTime) {
          this.silentPause();
          this.dispatchEvent(PLAYER_EVENTS.ENDED);
        }
      }

    });
    Object.defineProperty(this, 'playbackRate', {
      get() {
        return playbackRate;
      },

      set(newPlaybackRate) {
        if (typeof newPlaybackRate !== 'number') {
          return;
        }

        playbackRate = newPlaybackRate;
        this.dispatchEvent(PLAYER_EVENTS.PLAYBACK_RATE_CHANGE);
      }

    });

    if (this.autoplay) {
      this.play();
    }
  }

  get ended() {
    return this.endTime ? this.currentTime >= this.endTime : false;
  }

  get elapsed() {
    return this.currentTime - this.startTime;
  }

  get duration() {
    return this.endTime ? this.endTime - this.startTime : 0;
  }

  play() {
    if (this.silentPlay()) {
      this.dispatchEvent(PLAYER_EVENTS.PLAY);
    }
  }

  pause() {
    if (this.silentPause()) {
      this.dispatchEvent(PLAYER_EVENTS.PAUSE);
    }
  }
  /**
   * Set loop for specific time period
   * @param {PlayerLoopOptions} loop Player Loop options
   */


  setLoop(loop) {
    const newLoopConfig = { ...this.loop,
      ...loop
    };

    if (newLoopConfig.startTime > newLoopConfig.endTime) {
      newLoopConfig.startTime = Math.min(newLoopConfig.startTime, newLoopConfig.endTime);
      newLoopConfig.endTime = Math.min(newLoopConfig.startTime, newLoopConfig.endTime);
    }

    this.loop = newLoopConfig;
  }

  silentPlay() {
    if (this.playing) {
      return false;
    }

    this.playing = true;
    this[stepTimeRef] = Date.now();
    this[rafRef] = requestAnimationFrame(this.nextTick);
    return true;
  }

  silentPause() {
    if (!this.playing) {
      return false;
    }

    this.playing = false;
    cancelAnimationFrame(this[rafRef]);
    this[rafRef] = null;
    return true;
  }

  nextTick() {
    const now = Date.now();
    const step = now - this[stepTimeRef];
    let newTime = this.currentTime + step * this.playbackRate;

    if (this.loop.enabled && this.currentTime <= this.loop.endTime && newTime > this.loop.endTime) {
      newTime = this.loop.startTime;
    }

    this.currentTime = newTime;
    this[stepTimeRef] = now;

    if (this.playing) {
      this[rafRef] = requestAnimationFrame(this.nextTick);
    }
  }

  dispatchEvent(eventType) {
    if (typeof eventType === 'string') {
      return super.dispatchEvent(new Event(eventType));
    }

    if (eventType instanceof Event) {
      return super.dispatchEvent(eventType);
    }

    return false;
  }

}

export default Player;